import { NavLink, useLocation } from "react-router-dom";
import { Home, Users, Building2, Wallet, Receipt, UserCircle2, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { useAllowedMenus } from "@/hooks/useAllowedMenus";
import { usePortalNavEstado, type PortalNavItem } from "@/hooks/usePortalNav";

/**
 * Navegación del Portal de Embajadores.
 *
 * Vive en el LAYOUT, no dentro de `AmbassadorsPortalTab`: mientras estuvo dentro
 * de esa vista, cualquier ruta del portal que no fuera ese componente —el
 * Inventario, por ejemplo— se quedaba sin menú. Además la navegación era estado
 * local y no cambiaba la URL, así que ninguna pestaña era enlazable.
 *
 * Las pestañas salen de BD (menú 24, `submenus`): etiqueta y orden de BD, filtradas
 * por el permiso de lectura del rol. El ícono se resuelve por ruta.
 */
const BASE = "/admin/portal-embajador";
const EMBAJADOR_MENU_ID = 24;

const iconMap: Record<string, LucideIcon> = {
  [`${BASE}/inicio`]: Home,
  [`${BASE}/mis-referidos`]: Users,
  [`${BASE}/inventario`]: Building2,
  [`${BASE}/comisiones`]: Wallet,
  [`${BASE}/pagos`]: Receipt,
  [`${BASE}/perfil`]: UserCircle2,
};

/** Nav del portal desde BD, filtrada por permiso; `cargando` mientras llegan nav o permisos. */
function useEmbajadorNav(): { nav: PortalNavItem[]; cargando: boolean } {
  const { items, cargando } = usePortalNavEstado(EMBAJADOR_MENU_ID, iconMap, Home);
  const { isPathAllowed, isLoading } = useAllowedMenus();
  return { nav: items.filter((i) => isPathAllowed(i.path)), cargando: cargando || isLoading };
}

/** `/inventario/unidades` y `/inventario/proyecto/:id` marcan "Inventario". */
function useActivePath(nav: PortalNavItem[]) {
  const { pathname } = useLocation();
  const match = nav.filter((n) => pathname === n.path || pathname.startsWith(`${n.path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0];
  // `/registrar-referido` es la lista de referidos con el formulario abierto.
  if (!match && pathname.startsWith(`${BASE}/registrar-referido`)) return `${BASE}/mis-referidos`;
  return match?.path ?? `${BASE}/inicio`;
}

export function PortalEmbajadorNavDesktop() {
  const { nav, cargando } = useEmbajadorNav();
  const activePath = useActivePath(nav);
  if (cargando) {
    return (
      <div className="hidden md:flex items-center justify-center" aria-busy="true" aria-label="Cargando menú">
        <Skeleton className="h-12 w-[560px] max-w-full rounded-full" />
      </div>
    );
  }
  if (nav.length === 0) return null;
  return (
    <div className="hidden md:flex items-center justify-center">
      <nav
        className="inline-flex items-center gap-1 p-1.5 rounded-full border border-border bg-card shadow-sm"
        aria-label="Secciones del portal"
      >
        {nav.map((n) => {
          const Icon = n.icon;
          const isActive = activePath === n.path;
          return (
            <NavLink
              key={n.path}
              to={n.path}
              aria-current={isActive ? "page" : undefined}
              className={cn(
                "inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                isActive
                  ? "bg-primary text-primary-foreground shadow-md shadow-primary/20"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              )}
            >
              <Icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}

export function PortalEmbajadorNavMobile() {
  const { nav, cargando } = useEmbajadorNav();
  const activePath = useActivePath(nav);
  if (cargando) {
    return (
      <div
        className="md:hidden fixed left-1/2 -translate-x-1/2 z-40"
        style={{ bottom: "max(12px, env(safe-area-inset-bottom))", width: "min(94vw, 460px)" }}
        aria-busy="true"
        aria-label="Cargando menú"
      >
        <Skeleton className="h-14 w-full rounded-full" />
      </div>
    );
  }
  if (nav.length === 0) return null;
  return (
    <nav
      className="md:hidden fixed left-1/2 -translate-x-1/2 z-40 bg-card/95 backdrop-blur border border-border rounded-full shadow-xl shadow-black/10"
      style={{ bottom: "max(12px, env(safe-area-inset-bottom))", width: "min(94vw, 460px)" }}
      aria-label="Navegación principal del embajador"
    >
      <ul className="flex items-center justify-around p-1.5">
        {nav.map((n) => {
          const Icon = n.icon;
          const isActive = activePath === n.path;
          return (
            <li key={n.path} className="flex-1">
              <NavLink
                to={n.path}
                aria-current={isActive ? "page" : undefined}
                className={cn(
                  "w-full flex flex-col items-center gap-0.5 py-2 px-1 rounded-full transition-all",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  isActive
                    ? "bg-primary text-primary-foreground shadow-md shadow-primary/20"
                    : "text-muted-foreground active:bg-muted"
                )}
              >
                <Icon className="h-5 w-5" />
                <span className={cn("text-[10px]", isActive ? "font-semibold" : "font-medium")}>
                  {n.label}
                </span>
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
